import React, { useEffect, useState } from 'react';
import { useCoreRoute } from 'spark-core-dx/hooks';
import { useCoreContext, useAuthContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { ButtonControl } from 'spark-core-dx/components';
import PublicSiteHeader from '../components/PublicSiteHeader';

// Hero/feature assets
import HomeImage1 from '../../../content/homeImages/HomeImage1.png';
import HomeImage2 from '../../../content/homeImages/HomeImage2.jpg';
import HomeImage3 from '../../../content/homeImages/HomeImage3.png';
import HomeImage4 from '../../../content/homeImages/HomeImage4.png';
import HomeImage5 from '../../../content/homeImages/HomeImage5.png';
import HomeImage6 from '../../../content/homeImages/HomeImage6.png';

import EStyleLogo from '../../../content/homeImages/EstyleLogo.jpg';
import CentrifyLogo from '../../../content/homeImages/CentrifyLogo.png';
import AzureLogo from '../../../content/homeImages/Azure.png';
import SnowflakeLogo from '../../../content/homeImages/Snowflake.png';
import SFLogo from '../../../content/homeImages/salesforce.png';
import NetSuiteLogo from '../../../content/homeImages/Netsuite.png';
import BOLogo from '../../../content/homeImages/buildops-logo.jpeg';
import BigQueryLogo from '../../../content/homeImages/BigQuery.png';
import HeroBg from '../../../content/homeImages/hero.png';
import HeroBgLow from '../../../content/homeImages/hero-low.jpg';

const AUTHENTICATED_HOME_SCREEN_KEY = '/welcome/';

export const resolveAuthenticatedHomeScreenKey = (screens, requestedScreenKey) => {
  const requestedKey = typeof requestedScreenKey === 'string'
    ? requestedScreenKey.trim()
    : '';

  if (requestedKey && screens?.[requestedKey]) {
    return requestedKey;
  }

  return screens?.[AUTHENTICATED_HOME_SCREEN_KEY]
    ? AUTHENTICATED_HOME_SCREEN_KEY
    : null;
};

const Home = () => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { isAuthorized } = useAuthContext();
  const { coreNavigate } = useCoreRoute();
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const rootCoreState = coreState?.global ?? coreState;
  const globalNotLoading = rootCoreState?.get?.('globalNotLoading') ?? {};
  const forceGlobalLoading = rootCoreState?.get?.('forceGlobalLoading')?.forceGlobalLoading;
  const authCallBack = sessionStorage.getItem('AuthCallBack');
  const isStartupReady = Boolean(coreContext?.Launch)
    && coreContext?.applicationStatus === 2
    && Boolean(coreContext?.RoutingConfig)
    && globalNotLoading?.sparkAppStyle === true
    && globalNotLoading?.sparkIndexStyle === true
    && globalNotLoading?.userProfileConfig === true
    && !authCallBack
    && !forceGlobalLoading;
  const loginDisabledTitle = coreContext?.applicationStatus === 0
    ? 'Services are unavailable right now.'
    : 'Loading startup operations...';

  useEffect(() => {
    if (!isAuthorized) {
      return;
    }

    const destinationScreenKey = resolveAuthenticatedHomeScreenKey(
      coreContext?.screens,
      coreContext?.RedirectScreenKey
    );

    if (destinationScreenKey) {
      coreNavigate(destinationScreenKey);
    }
  }, [coreContext?.RedirectScreenKey, coreContext?.screens, coreNavigate, isAuthorized]);

  useEffect(() => {
    const image = new Image();
    image.onload = () => setHeroImageLoaded(true);
    image.src = HeroBg;

    if (image.complete) {
      setHeroImageLoaded(true);
    }

    return () => {
      image.onload = null;
    };
  }, []);

  if (isAuthorized) {
    return null;
  }

  const goLogin = () => {
    if (!isStartupReady) {
      return;
    }
    coreNavigate('/login/');
  };

  const handleNav = (e, target) => {
    e.preventDefault();
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="homepage vertical-scrollable">
      <PublicSiteHeader />

      {/* ===== 1) Product / Marketing ===== */}
      <section
        id="landing"
        className={`hero ${heroImageLoaded ? 'hero--loaded' : ''}`}
        style={{
          '--hero-preview-image': `url(${HeroBgLow})`,
          '--hero-image': `url(${HeroBg})`
        }}
      >
        <div className="hero-content">
          <h1>CONVOKE DATA EXPERIENCE</h1>
          <h2>AI-assisted data, reporting, and analytics—on your terms.</h2>
          <p className="tagline">
            Bring every source together in a single platform to connect, map, and analyze your data—without heavy engineering or long consulting cycles
          </p>

        <div className="hero-ctas">
            <a className="btn-primary" href="#about" onClick={(e) => handleNav(e, '#about')}>Explore the Product</a>
            <a className="btn-outline" href="#consulting" onClick={(e) => handleNav(e, '#consulting')}>Consulting</a>
          </div>
        </div>
      </section>

      {/* ===== Product Deep-Dive ===== */}
      <section id="about" className="section section-contrast">
        <div className="section-inner">
          <h2>Centrify Solutions</h2>
          <p className="section-lede">
            Unify integration, orchestration, governance, and AI-driven analytics in one platform. Connect Snowflake, BigQuery,
            Azure SQL/Fabric, files, ERPs, CRMs, and more—then let AI help you model, query, and deliver outcomes.
          </p>

          <div className="feature-grid">
            <article className="feature">
              <img src={HomeImage3} alt="Integrations" />
              <h3>Integrations</h3>
              <p>Connect once, reuse everywhere: warehouses, lakes, apps, and exports.</p>
              <ul>
                <li>Reusable connections & secrets</li>
                <li>Connect to anything</li>
                <li>Schema discovery & catalog</li>
              </ul>
            </article>

            <article className="feature">
              <img src={HomeImage5} alt="Pipelines & Observability" />
              <h3>Data Pipelines</h3>
              <p>Configure, map, and analyze your data with no development required.</p>
              <ul>
                <li>Configure your source systems</li>
                <li>Map and analyze</li>
                <li>Track data changes</li>
              </ul>
            </article>

            <article className="feature">
              <img src={HomeImage4} alt="AI Orchestration" />
              <h3>AI Orchestration</h3>
              <p>Generate T-SQL and DAX, summarize results, and automate next steps—on your data.</p>
              <ul>
                <li>Strict JSON directives</li>
                <li>Few-shot query patterns</li>
                <li>Summaries & lineage notes</li>
              </ul>
            </article>

            <article className="feature">
              <img src={HomeImage6} alt="Security & Governance" />
              <h3>Security</h3>
              <p>Least-privilege in Azure. No wholesale data saved into our systems. You control data and policies.</p>
              <ul>
                <li>Azure Entra ID & RBAC</li>
                <li>Per-environment isolation (on request)</li>
                <li>Auditable logs</li>
              </ul>
            </article>
          </div>

          {/* Integrations & Partners band */}
          <div className="integrations-band">
            <h3>Integrations &amp; Partners</h3>
            <p className="band-lede">Plug into your ecosystem—then scale confidently.</p>
            <div className="logo-row">
              <img src={AzureLogo} alt="Microsoft Azure" />
              <img src={SFLogo} alt="SalesForce" />
              <img src={SnowflakeLogo} alt="Snowflake" />
              <img src={NetSuiteLogo} alt="NetSuite" />
              <img src={BigQueryLogo} alt="Google BigQuery" />
              <img src={BOLogo} alt="BuildOps" />
              <div className="more-tile">...and more</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2) Consulting (image left @ 25vw, under header) ===== */}
      <section id="consulting" className="section">
        <div className="section-inner">
          <h2>Consulting</h2>
          <p className="section-lede">
            Our team helps you organize, report, and gain access to your data through configuration and setup—not
            endless custom development. We align Convoke DX to your stack, govern it properly, and hand you a system
            your teams can actually run.
          </p>

          {/* Split layout: image left (25vw), content right */}
          <div
            className="consulting-split"
            style={{
              display: 'grid',
              gridTemplateColumns: '25vw 1fr',
              gap: '2rem',
              alignItems: 'start',
              marginTop: '1.25rem'
            }}
          >
            {/* Left: image at 25% of screen */}
            <div className="consulting-art hide-on-mobile" aria-hidden="true">
              <img
                src={HomeImage2}
                alt="Consulting overview"
                style={{ width: '25vw', maxWidth: '25vw', height: 'auto', display: 'block' }}
              />
            </div>

            {/* Right: existing consulting content */}
            <div className="consulting-content">
              <div className="card-grid">
                <article className="card card-with-image">
                  <h3>Quickstart</h3>
                  <p>We configure, map, and onboard multiple integration sources in weeks—not months.</p>
                  <ul className="sq-bullets">
                    <li>Connect your systems</li>
                    <li>Design schemas and mappings</li>
                    <li>Reporting & analytics workshops</li>
                  </ul>
                </article>

                <article className="card">
                  <h3>AI Accelerator</h3>
                  <p>Stop writing reports and start getting answers. Ask your data questions and get actionable results.</p>
                  <ul className="sq-bullets">
                    <li>AI data-mapping assistant</li>
                    <li>Prompt design</li>
                    <li>AI-driven report analysis</li>
                  </ul>
                </article>

                <article className="card card-with-image">
                  <h3>Modernization</h3>
                  <p>Modernize legacy integrations from multiple vendors into a single AI-enabled platform.</p>
                  <ul className="sq-bullets">
                    <li>Refactor brittle ETL to data mappings</li>
                    <li>Add observability & SLAs</li>
                    <li>Consolidate onto a single platform</li>
                  </ul>
                </article>

                <article className="card">
                  <h3>Configuration</h3>
                  <p>Environments, security, and configurations aligned to your operating model and compliance needs.</p>
                  <ul>
                    <li>Sources → Schemas → Warehouse & AI</li>
                    <li>Security and reporting in one place</li>
                    <li>Access via your Microsoft 365 account</li>
                  </ul>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3) Customers & Partners (header first, then split with image right @ 25vw) ===== */}
      <section id="partners" className="section section-contrast">
        <div className="section-inner">
          {/* Header sits above, like Consulting */}
          <h2>Customers &amp; Partners</h2>
          <p className="section-lede">We collaborate with teams who care about speed, trust, and outcomes.</p>

          {/* Now the split lives UNDER the header */}
          <div
            className="partners-split"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 25vw',
              gap: '2rem',
              alignItems: 'start',
              marginTop: '1.25rem'
            }}
          >
            {/* Left: content/cards */}
            <div className="partners-content">
              <div className="partner-grid">
                {/* EStyle — customer */}
                <div className="partner-card">
                  <div className="partner-header">
                    <img src={EStyleLogo} alt="EStyle" />
                    <div className="partner-type">Customer</div>
                  </div>
                  <p>Enterprise fashion e-commerce & operations analytics.</p>
                  <blockquote className="testimonial">
                    “Convoke DX gave us governed access to our data in days, not months. Our teams answer their own
                    questions without waiting in line.”
                    <cite>— Tony Baldwin, CIO </cite>
                  </blockquote>
                  <a className="partner-link" href="https://www.estyle.com/" target="_blank" rel="noreferrer">
                    estyle.com ↗
                  </a>
                </div>

                {/* Centrify — partner */}
                <div className="partner-card">
                  <div className="partner-header">
                    <img src={CentrifyLogo} alt="Centrify Solutions" />
                    <div className="partner-type">Partner</div>
                  </div>
                  <p>Engineering support for automation, analytics delivery, and applied AI.</p>

                  <blockquote className="testimonial">
                    “Convoke DX delivers AI-driven analytics and data solutions other platforms can’t match.”
                    <cite>— Tony Baldwin, Owner</cite>
                  </blockquote>
                  <a className="partner-link" href="https://www.centrifysolutions.com/" target="_blank" rel="noreferrer">
                    centrifysolutions.com ↗
                  </a>
                </div>

                {/* CTA */}
                <div className="partner-card cta-card">
                  <div className="cta-title">Get started now</div>
                  <p>Ready to see your data in motion? We’ll configure sources, deliver a mart, and publish a first insight.</p>
                  <a
                    className="btn-primary"
                    href="mailto:info@ConvokeDX.com?subject=Request%20a%20demo"
                  >
                    Request a demo
                  </a>
                </div>
              </div>
            </div>

            {/* Right: image at 25% of screen, hidden on mobile */}
            <div className="partners-art hide-on-mobile" aria-hidden="true">
              <img
                src={HomeImage1}
                alt="Customers and partners visual"
                style={{ width: '25vw', maxWidth: '25vw', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 4) More Information ===== */}
      <section id="contact" className="section">
        <div className="section-inner">
          <h2>More Information</h2>
          <p className="section-lede">
            Want a tailored walkthrough of Convoke DX? Request a demo and we’ll show how the product, setup, and consulting
            services align to your stack. We’ll discuss mapping your sources, outline a quickstart, and leave you with clear next steps.
          </p>

          <div className="contact-box">
            <div>
              <div className="contact-label">Email</div>
              <a
                href="mailto:info@ConvokeDX.com?subject=Get%20more%20information"
                className="contact-value"
              >
                info@ConvokeDX.com
              </a>
            </div>
            <div className="contact-actions">
              <a
                className="btn-primary"
                href="mailto:info@ConvokeDX.com?subject=Get%20more%20information"
              >
                Contact Us
              </a>
              <ButtonControl
                className="btn-outline"
                disabled={!isStartupReady}
                disabledTitle={loginDisabledTitle}
                onClick={goLogin}
              >
                Login
              </ButtonControl>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div>© {new Date().getFullYear()} Convoke DX • All rights reserved.</div>
        <div className="footer-links">
          <a href="#about" onClick={(e) => handleNav(e, '#about')}>Product</a>
          <a href="#consulting" onClick={(e) => handleNav(e, '#consulting')}>Consulting</a>
          <a href="#partners" onClick={(e) => handleNav(e, '#partners')}>Customers &amp; Partners</a>
          <a href="#contact" onClick={(e) => handleNav(e, '#contact')}>More Info</a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
